import React, { useCallback, useEffect, useRef, useState } from 'react'
import {
  ArrowDownTrayIcon,
  ChevronDownIcon,
  ChevronUpIcon,
  FolderIcon,
  InformationCircleIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline'
import { Button, Heading, Text, Tooltip } from '../ui'
import {
  folderDisplayName,
  formatBytes,
  isShowDirectoryPickerWriteAvailable,
  pickBackupDirectory,
  runStorageBackup,
  runStorageBackupAll,
  storageBackupService,
} from '../../services/storageBackup'
import type {
  StorageBackupProgress,
  StorageBucketFolders,
  StorageFolderRow,
} from '../../types/storageBackup'

const HELP_STORAGE_FOLDERS = (
  <div className="space-y-1.5 text-left">
    <p className="font-semibold">Backup das pastas do Storage</p>
    <p>
      Lista as pastas de primeiro nível de cada bucket. O backup grava os arquivos numa pasta
      escolhida no computador (Chrome ou Edge), preservando a estrutura bucket/pasta/...
    </p>
    <p>
      Use Backup de todos para baixar todos os buckets de uma vez. Arquivos na raiz aparecem como
      (raiz). Placeholders de pasta vazia não são baixados.
    </p>
  </div>
)

const InfoTip: React.FC<{ content: React.ReactNode; label?: string }> = ({
  content,
  label = 'Saiba mais',
}) => (
  <Tooltip content={content} position="bottom" constrainWidth className="inline-flex shrink-0 align-middle">
    <button
      type="button"
      className="inline-flex rounded-full p-0.5 text-gray-500 hover:text-gray-800 dark:text-gray-400 dark:hover:text-gray-200 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 dark:focus:ring-offset-gray-900"
      aria-label={label}
    >
      <InformationCircleIcon className="h-4 w-4" />
    </button>
  </Tooltip>
)

type Props = {
  /** Incrementar para forçar recarga (ex.: botão Atualizar da página). */
  reloadKey?: number
}

export const StorageFoldersBackup: React.FC<Props> = ({ reloadKey = 0 }) => {
  const [buckets, setBuckets] = useState<StorageBucketFolders[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [sectionExpanded, setSectionExpanded] = useState(false)
  const [expandedBuckets, setExpandedBuckets] = useState<Set<string>>(new Set())
  const [pickerAvailable] = useState(() => isShowDirectoryPickerWriteAvailable())

  const [backingUpKey, setBackingUpKey] = useState<string | null>(null)
  const [progress, setProgress] = useState<StorageBackupProgress | null>(null)
  const [backupMessage, setBackupMessage] = useState<string | null>(null)
  const abortRef = useRef<AbortController | null>(null)
  const startLockRef = useRef(false)

  const load = useCallback(async () => {
    try {
      setLoading(true)
      setError(null)
      const data = await storageBackupService.listFolders()
      setBuckets(data)
      setExpandedBuckets((prev) => {
        if (prev.size > 0) return prev
        if (data.length === 1) return new Set([data[0].bucketId])
        return prev
      })
    } catch (e: unknown) {
      const msg = e instanceof Error ? e.message : 'Erro ao listar pastas do Storage'
      setError(msg)
      setBuckets([])
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    void load()
  }, [load, reloadKey])

  useEffect(() => {
    return () => {
      abortRef.current?.abort()
    }
  }, [])

  const toggleBucket = (bucketId: string) => {
    setExpandedBuckets((prev) => {
      const next = new Set(prev)
      if (next.has(bucketId)) next.delete(bucketId)
      else next.add(bucketId)
      return next
    })
  }

  const cancelBackup = () => {
    abortRef.current?.abort()
  }

  const startBackup = async (opts: {
    key: string
    bucket: string
    prefix?: string | null
    rootOnly?: boolean
    estimatedTotal: number
  }) => {
    if (!pickerAvailable || backingUpKey || startLockRef.current) return
    startLockRef.current = true
    setSectionExpanded(true)

    // Trava a UI antes do seletor (evita double-click → "File picker already active")
    setBackingUpKey(opts.key)
    setBackupMessage(null)
    setProgress({ done: 0, total: opts.estimatedTotal, currentPath: null, failed: [] })

    let rootDir
    try {
      rootDir = await pickBackupDirectory()
    } catch (e: unknown) {
      const msg = e instanceof Error ? e.message : 'Não foi possível abrir o seletor de pasta'
      setBackupMessage(msg)
      setProgress(null)
      setBackingUpKey(null)
      startLockRef.current = false
      return
    }

    if (!rootDir) {
      setProgress(null)
      setBackingUpKey(null)
      startLockRef.current = false
      return
    }

    const controller = new AbortController()
    abortRef.current = controller

    try {
      const result = await runStorageBackup({
        request: {
          bucket: opts.bucket,
          prefix: opts.prefix,
          rootOnly: opts.rootOnly,
          estimatedTotal: opts.estimatedTotal,
        },
        rootDir,
        signal: controller.signal,
        onProgress: setProgress,
      })

      const failCount = result.failed.length
      if (failCount === 0) {
        setBackupMessage(`Backup concluído: ${result.done} arquivo(s) gravado(s).`)
      } else {
        setBackupMessage(
          `Backup concluído com avisos: ${result.done - failCount} ok, ${failCount} falha(s).`,
        )
      }
    } catch (e: unknown) {
      if (e instanceof DOMException && e.name === 'AbortError') {
        setBackupMessage('Backup cancelado.')
      } else {
        const msg = e instanceof Error ? e.message : 'Erro ao executar backup'
        setBackupMessage(msg)
      }
    } finally {
      abortRef.current = null
      setBackingUpKey(null)
      startLockRef.current = false
    }
  }

  const backupFolder = (bucketId: string, folder: StorageFolderRow) => {
    const isRoot = folder.folder_name == null
    const key = `${bucketId}::${isRoot ? '__root__' : folder.folder_name}`
    void startBackup({
      key,
      bucket: bucketId,
      prefix: isRoot ? null : folder.folder_name,
      rootOnly: isRoot,
      estimatedTotal: folder.file_count,
    })
  }

  const backupBucket = (bucket: StorageBucketFolders) => {
    void startBackup({
      key: `${bucket.bucketId}::*`,
      bucket: bucket.bucketId,
      prefix: null,
      rootOnly: false,
      estimatedTotal: bucket.totalFiles,
    })
  }

  const backupAll = async () => {
    const withFiles = buckets.filter((b) => b.totalFiles > 0)
    if (!pickerAvailable || backingUpKey || startLockRef.current || withFiles.length === 0) return
    startLockRef.current = true
    setSectionExpanded(true)

    const estimatedTotal = withFiles.reduce((s, b) => s + b.totalFiles, 0)
    setBackingUpKey('*::*')
    setBackupMessage(null)
    setProgress({ done: 0, total: estimatedTotal, currentPath: null, failed: [] })

    let rootDir
    try {
      rootDir = await pickBackupDirectory()
    } catch (e: unknown) {
      const msg = e instanceof Error ? e.message : 'Não foi possível abrir o seletor de pasta'
      setBackupMessage(msg)
      setProgress(null)
      setBackingUpKey(null)
      startLockRef.current = false
      return
    }

    if (!rootDir) {
      setProgress(null)
      setBackingUpKey(null)
      startLockRef.current = false
      return
    }

    const controller = new AbortController()
    abortRef.current = controller

    try {
      const result = await runStorageBackupAll({
        requests: withFiles.map((b) => ({
          bucket: b.bucketId,
          prefix: null,
          rootOnly: false,
          estimatedTotal: b.totalFiles,
        })),
        rootDir,
        signal: controller.signal,
        onProgress: setProgress,
      })

      const failCount = result.failed.length
      if (failCount === 0) {
        setBackupMessage(
          `Backup concluído: ${result.done} arquivo(s) de ${withFiles.length} bucket(s).`,
        )
      } else {
        setBackupMessage(
          `Backup concluído com avisos: ${result.done - failCount} ok, ${failCount} falha(s) em ${withFiles.length} bucket(s).`,
        )
      }
    } catch (e: unknown) {
      if (e instanceof DOMException && e.name === 'AbortError') {
        setBackupMessage('Backup cancelado.')
      } else {
        const msg = e instanceof Error ? e.message : 'Erro ao executar backup'
        setBackupMessage(msg)
      }
    } finally {
      abortRef.current = null
      setBackingUpKey(null)
      startLockRef.current = false
    }
  }

  const totalFilesAll = buckets.reduce((s, b) => s + b.totalFiles, 0)

  const progressPct =
    progress && progress.total > 0
      ? Math.min(100, Math.round((100 * progress.done) / progress.total))
      : 0

  return (
    <div className="px-6 py-6 border-t border-gray-200 dark:border-gray-700">
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div className="flex items-start min-w-0">
              <FolderIcon className="h-6 w-6 mr-2 text-gray-600 dark:text-gray-400 shrink-0 mt-0.5" aria-hidden />
              <div className="min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <Heading as="h2" size="2xl" className="text-gray-900 dark:text-white">
                    Backup das pastas do Storage
                  </Heading>
                  <InfoTip content={HELP_STORAGE_FOLDERS} label="Sobre o backup do Storage" />
                </div>
                {!loading && !error && buckets.length > 0 && (
                  <Text size="xs" color="muted" className="mt-1">
                    {buckets.length} bucket(s) · {totalFilesAll} arquivo(s)
                  </Text>
                )}
              </div>
            </div>
            <div className="flex items-center gap-3 shrink-0">
              {sectionExpanded && (
                <Button
                  type="button"
                  variant="outline"
                  disabled={!pickerAvailable || Boolean(backingUpKey) || loading || totalFilesAll === 0}
                  onClick={() => void backupAll()}
                  className="inline-flex items-center gap-1.5 text-sm"
                  title="Baixar todos os buckets para uma pasta local"
                >
                  <ArrowDownTrayIcon className={`h-4 w-4 ${backingUpKey === '*::*' ? 'animate-pulse' : ''}`} />
                  Backup de todos
                </Button>
              )}
              <button
                type="button"
                onClick={() => setSectionExpanded((v) => !v)}
                className="flex items-center gap-1 text-sm text-primary-600 dark:text-primary-400 hover:text-primary-800 dark:hover:text-primary-300 transition-colors"
              >
                {sectionExpanded ? (
                  <>
                    <ChevronUpIcon className="h-5 w-5" />
                    Ocultar
                  </>
                ) : (
                  <>
                    <ChevronDownIcon className="h-5 w-5" />
                    Ver{!loading && buckets.length > 0 ? ` (${buckets.length})` : ''}
                  </>
                )}
              </button>
            </div>
          </div>
        </div>

        {sectionExpanded && (
          <div className="px-6 py-4 space-y-4">
            {!pickerAvailable && (
              <div className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 dark:border-amber-800 dark:bg-amber-900/20">
                <Text size="sm" className="text-amber-800 dark:text-amber-200">
                  O backup local exige Chrome ou Edge (seletor de pasta com permissão de escrita). Neste
                  navegador os botões de backup ficam desabilitados.
                </Text>
              </div>
            )}

            {backingUpKey && progress && (
              <div className="rounded-lg border border-gray-200 bg-gray-50 p-4 dark:border-gray-700 dark:bg-gray-900/40">
                <div className="flex items-center justify-between gap-3 mb-2">
                  <Text size="sm" className="text-gray-900 dark:text-white font-medium">
                    Backup em andamento: {progress.done} / {progress.total}
                  </Text>
                  <Button
                    type="button"
                    variant="outline"
                    onClick={cancelBackup}
                    className="inline-flex items-center gap-1 text-xs"
                  >
                    <XMarkIcon className="h-4 w-4" />
                    Cancelar
                  </Button>
                </div>
                <div className="h-2 w-full overflow-hidden rounded-full bg-gray-200 dark:bg-gray-700">
                  <div
                    className="h-full rounded-full bg-primary-600 transition-all duration-300"
                    style={{ width: `${progressPct}%` }}
                  />
                </div>
                {progress.currentPath && (
                  <Text size="xs" color="muted" className="mt-2 font-mono truncate">
                    {progress.currentPath}
                  </Text>
                )}
                {progress.failed.length > 0 && (
                  <Text size="xs" className="mt-1 text-amber-700 dark:text-amber-300">
                    {progress.failed.length} falha(s) até agora
                  </Text>
                )}
              </div>
            )}

            {backupMessage && !backingUpKey && (
              <div className="rounded-lg border border-gray-200 bg-gray-50 px-4 py-3 dark:border-gray-700 dark:bg-gray-900/40">
                <Text size="sm" className="text-gray-800 dark:text-gray-200">
                  {backupMessage}
                </Text>
                {progress && progress.failed.length > 0 && (
                  <ul className="mt-2 max-h-32 overflow-y-auto space-y-1">
                    {progress.failed.slice(0, 20).map((f) => (
                      <li key={f.path}>
                        <Text size="xs" className="font-mono text-red-700 dark:text-red-300">
                          {f.path}: {f.error}
                        </Text>
                      </li>
                    ))}
                    {progress.failed.length > 20 && (
                      <li>
                        <Text size="xs" color="muted">
                          … e mais {progress.failed.length - 20}
                        </Text>
                      </li>
                    )}
                  </ul>
                )}
              </div>
            )}

            {loading ? (
              <div className="p-6 animate-pulse h-24 rounded-lg bg-gray-100 dark:bg-gray-700" />
            ) : error ? (
              <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg p-4">
                <Text size="sm" className="text-yellow-800 dark:text-yellow-200">
                  {error}
                </Text>
                <Text size="xs" color="muted" className="mt-2">
                  Se a função list_storage_folders ainda não existir, execute o SQL em
                  frontend/sqls/monitoramento_storage_folders.sql no Supabase SQL Editor.
                </Text>
              </div>
            ) : buckets.length === 0 ? (
              <div className="px-2 py-6 text-center">
                <Text size="sm" color="muted">
                  Nenhum arquivo encontrado no Storage.
                </Text>
              </div>
            ) : (
              <div className="space-y-4">
                {buckets.map((bucket) => {
                  const expanded = expandedBuckets.has(bucket.bucketId)
                  const bucketBusy = backingUpKey?.startsWith(`${bucket.bucketId}::`) ?? false

                  return (
                    <div
                      key={bucket.bucketId}
                      className="rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden"
                    >
                      <div className="px-4 py-3 border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900/40">
                        <div className="flex flex-wrap items-center justify-between gap-3">
                          <div className="min-w-0">
                            <Text size="sm" className="text-gray-900 dark:text-white font-mono font-semibold">
                              {bucket.bucketId}
                            </Text>
                            <Text size="xs" color="muted" className="mt-0.5">
                              {bucket.folders.length} pasta(s) · {bucket.totalFiles} arquivo(s) ·{' '}
                              {formatBytes(bucket.totalSizeBytes)}
                            </Text>
                          </div>
                          <div className="flex items-center gap-2 shrink-0">
                            <Button
                              type="button"
                              variant="outline"
                              disabled={!pickerAvailable || Boolean(backingUpKey)}
                              onClick={() => backupBucket(bucket)}
                              className="inline-flex items-center gap-1.5 text-sm"
                              title="Baixar o bucket inteiro para uma pasta local"
                            >
                              <ArrowDownTrayIcon className="h-4 w-4" />
                              Backup do bucket
                            </Button>
                            <button
                              type="button"
                              onClick={() => toggleBucket(bucket.bucketId)}
                              className="flex items-center gap-1 text-sm text-primary-600 dark:text-primary-400 hover:text-primary-800 dark:hover:text-primary-300 transition-colors"
                            >
                              {expanded ? (
                                <>
                                  <ChevronUpIcon className="h-5 w-5" />
                                  Ocultar
                                </>
                              ) : (
                                <>
                                  <ChevronDownIcon className="h-5 w-5" />
                                  Ver pastas ({bucket.folders.length})
                                </>
                              )}
                            </button>
                          </div>
                        </div>
                      </div>

                      {expanded && (
                        <div className="overflow-x-auto">
                          <table className="w-full divide-y divide-gray-200 dark:divide-gray-700">
                            <thead className="bg-gray-50 dark:bg-gray-700">
                              <tr>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                                  Pasta
                                </th>
                                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                                  Arquivos
                                </th>
                                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                                  Tamanho
                                </th>
                                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                                  Ação
                                </th>
                              </tr>
                            </thead>
                            <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                              {bucket.folders.map((folder) => {
                                const isRoot = folder.folder_name == null
                                const rowKey = `${bucket.bucketId}::${isRoot ? '__root__' : folder.folder_name}`
                                const rowBusy = backingUpKey === rowKey

                                return (
                                  <tr
                                    key={rowKey}
                                    className="hover:bg-gray-50 dark:hover:bg-gray-700"
                                  >
                                    <td className="px-6 py-4 whitespace-nowrap">
                                      <Text
                                        size="sm"
                                        className={`font-mono ${
                                          isRoot
                                            ? 'text-gray-500 dark:text-gray-400 italic'
                                            : 'text-gray-900 dark:text-white'
                                        }`}
                                      >
                                        {folderDisplayName(folder.folder_name)}
                                      </Text>
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap text-right">
                                      <Text size="sm" className="text-gray-900 dark:text-white">
                                        {folder.file_count}
                                      </Text>
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap text-right">
                                      <Text size="sm" className="text-gray-900 dark:text-white font-semibold">
                                        {formatBytes(folder.total_size_bytes)}
                                      </Text>
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap text-right">
                                      <Button
                                        type="button"
                                        variant="outline"
                                        disabled={
                                          !pickerAvailable ||
                                          Boolean(backingUpKey) ||
                                          folder.file_count === 0
                                        }
                                        onClick={() => backupFolder(bucket.bucketId, folder)}
                                        className="inline-flex items-center gap-1.5 text-xs"
                                      >
                                        <ArrowDownTrayIcon
                                          className={`h-4 w-4 ${rowBusy || bucketBusy ? 'animate-pulse' : ''}`}
                                        />
                                        Backup
                                      </Button>
                                    </td>
                                  </tr>
                                )
                              })}
                            </tbody>
                          </table>
                        </div>
                      )}
                    </div>
                  )
                })}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  )
}

export default StorageFoldersBackup
